import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SearchIcon from '@mui/icons-material/Search';
import { useRef, useState } from 'react';

/**
 * Props for the SearchInput component
 * @param {string} value - The current search value
 * @param {(value: string) => void} onChange - Function to handle search value changes
 * @param {string} placeholder - Placeholder text when input is empty
 * @param {string} focusPlaceholder - Placeholder text when input is focused
 * @param {() => void} onEnterPress - Optional callback for when Enter key is pressed
 * @param {string} className - Optional additional CSS classes
 * @param {string} width - Optional width override (default is 'w-full')
 */
type SearchInputProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  focusPlaceholder?: string;
  onEnterPress?: () => void;
  className?: string;
  width?: string;
  autoFocus?: boolean;
  bgColor?: string;
};

/**
 * A reusable search input component with clear functionality
 */
export const SearchInput = ({
  value,
  onChange,
  placeholder,
  focusPlaceholder,
  onEnterPress,
  className = '',
  width = 'w-full',
  autoFocus = false,
  bgColor,
}: SearchInputProps) => {
  const [isSearchFocused, setIsSearchFocused] = useState<boolean>(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onEnterPress) {
      e.preventDefault();
      onEnterPress();
    }
  };

  return (
    <div className={`group relative ${width} ${className}`}>
      <input
        ref={searchInputRef}
        type='text'
        placeholder={placeholder}
        onFocus={(e) => {
          setIsSearchFocused(true);
          if (focusPlaceholder) {
            e.target.placeholder = focusPlaceholder;
          }
        }}
        onBlur={(e) => {
          setIsSearchFocused(false);
          e.target.placeholder = placeholder;
        }}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        value={value}
        autoFocus={autoFocus}
        className={`${bgColor ? bgColor : 'group-hover:bg-gray-300 group-hover:focus:bg-cloudGray bg-cloudGray'} h-10 w-full rounded border-none py-2 pl-3 pr-8 font-secondary font-semibold leading-[24px] text-primary-dark outline-none transition-all selection:bg-primary selection:text-cloudGray placeholder:font-normal placeholder:text-[#636E7A] focus:ring-2 focus:ring-primary xs:h-12`}
      />
      {isSearchFocused ? (
        <div
          // mousedown fires before the onBlur for the above input. So, it will clear in the input AND retain focus on the input.
          onMouseDown={(e) => {
            e.preventDefault();
            onChange('');
          }}
          className='absolute right-2 top-1.5 cursor-pointer fill-darkSteel hover:fill-gray-400 active:scale-[94%] xs:top-2.5'
        >
          <HighlightOffIcon className='transition-all' style={{ fontSize: '24px', fill: 'inherit' }} />
        </div>
      ) : (
        <div
          onClick={() => searchInputRef.current?.focus()}
          className='absolute right-2 top-1.5 cursor-pointer fill-darkSteel hover:fill-gray-400 xs:top-2.5'
        >
          <SearchIcon className='transition-all' style={{ fontSize: '24px', fill: 'inherit' }} />
        </div>
      )}
    </div>
  );
};

export default SearchInput;
